import { useEffect, useMemo, useState } from "react";
import {
  Baseball,
  Calendar,
  CalendarCheck,
  EditOne,
  GitMerge,
  ListNumber,
  Location,
  Menu,
  Plus,
  Question,
  Ribbon,
  SeaWaves,
  Share,
  CreditCard,
  Tag as TagIcon,
} from "@mynaui/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ITournament } from "../../models/ITournament";
import { CircleDot, Sun, Target, Trophy, Volleyball, X } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFutbol } from "@fortawesome/free-solid-svg-icons";
import TournamentEditDialog from "../TournamentEditDialog";
import moment from "moment";
import DemographicsBar from "../DemographicsBar";
import { IPlayerDemographic, IPlayerRegistrationTrend } from "../../models/IPlayer";
import { ChartItem, GroupData } from "../../types/chart";
import { getColorForKey, sportTypeToSlug } from "../../utils/methods";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getPlayerDemographics, getPlayerRegistrationTrends } from "../../api/players";
import { editTournament, deleteTournament } from "../../api/tournaments";
import {
  getPaymentAccountsByTournament,
  getAllPaymentAccounts,
  linkPaymentAccountToTournament,
  unlinkPaymentAccountFromTournament,
} from "../../api/paymentAccounts";
import { TournamentPaymentAccountDTO, PaymentAccountDTO, PaymentAccountTypeLabels } from "../../models/IPaymentAccount";
import { getPromoCodesByTournament } from "../../api/promoCodes";
import { IPromoCode } from "../../models/IPromoCode";
import { getAccommodationsByTournament } from "../../api/accommodations";
import { IAccommodation } from "../../models/IAccommodation";
import { useAppSelector, useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { canManageTournaments, canManageCategories, isSuperAdmin } from "../../utils/authorization";
import { Building, Trash2 } from "lucide-react";
import TournamentGalleryCard from "../TournamentGalleryCard";
import DeleteTournamentDialog from "../DeleteTournamentDialog";
import { Button, Card, EmptyState, IconButton, Loader, PageHeader, Select, StatCard, Tag, useDialogs } from "../../ui";
import type { TagTone } from "../../ui";
import "./styles.scss";

// Recharts paints with attributes, so the brand tokens are spelled out here.
const CHART = {
  bar: "#2c42c3", // --brand
  grid: "#e4e4e7", // --line
  axis: "#5c5f66", // --muted
  ink: "#0e1116", // --ink
  cursor: "#f4f4f5", // --alt
};

const REGISTRATION_TONES: Record<string, TagTone> = { open: "ok", closed: "danger", "not-open": "warn" };

// The sport tag: padel blue, volleyball orange, beach volleyball yellow, the rest neutral.
const SPORT_TONES: Record<string, TagTone> = { Padel: "brand", Volleyball: "spark", "Beach Volleyball": "yellow" };

type TournamentDetailProps = {
  item: ITournament;
  onBack: (count: number) => void;
  onDoneEdit: () => void;
};

const TournamentDetail = ({ item, onBack, onDoneEdit }: TournamentDetailProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const { alert } = useDialogs();

  const [edit, setEdit] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [readMore, setIsReadMore] = useState(false);

  const [playersCount, setPlayersCount] = useState(0);
  const [registrationTrendOption, setRegistrationTrendOption] = useState("thisMonth");
  const [playerDemographicOption, setPlayerDemographicOption] = useState("nationality");
  const [playerDemographics, setPlayerDemographics] = useState<IPlayerDemographic>();
  const [playerRegistrationTrends, setPlayerRegistrationTrends] = useState<IPlayerRegistrationTrend>();
  const [registrationStatus, setRegistrationStatus] = useState(
    item.registrationOpen ? "open" : item.registrationClosed ? "closed" : "not-open"
  );
  const [paymentAccounts, setPaymentAccounts] = useState<TournamentPaymentAccountDTO[]>([]);
  const [paymentAccountsLoading, setPaymentAccountsLoading] = useState(true);
  const [allPaymentAccounts, setAllPaymentAccounts] = useState<PaymentAccountDTO[]>([]);
  const [showLinkSelect, setShowLinkSelect] = useState(false);
  const [promoCodes, setPromoCodes] = useState<IPromoCode[]>([]);
  const [promoCodesLoading, setPromoCodesLoading] = useState(true);
  const [accommodations, setAccommodations] = useState<IAccommodation[]>([]);
  const [accommodationsLoading, setAccommodationsLoading] = useState(true);

  const handleBack = () => {
    const count = searchParams.get("count");
    onBack(Number(count));
  };

  const getBackButtonText = () => {
    const goTo = searchParams.get("goTo");
    const playerId = searchParams.get("playerId");

    if (goTo === "player" && playerId) {
      return "Back";
    }
    return "Back to Tournaments";
  };

  const getChartData = () => {
    switch (registrationTrendOption) {
      case "thisMonth":
        return { data: playerRegistrationTrends?.thisMonth, xKey: "label" };
      case "monthly":
        return { data: playerRegistrationTrends?.monthly, xKey: "label" };
      case "yearly":
        return { data: playerRegistrationTrends?.yearly, xKey: "label" };
      default:
        return { data: [], xKey: "" };
    }
  };

  const getPlayerDemographicData = async () => {
    try {
      const res = await getPlayerDemographics(item.id);
      setPlayersCount(res.data.totalPlayers);
      setPlayerDemographics(res.data);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getPlayerRegistrationTrendsData = async () => {
    try {
      const res = await getPlayerRegistrationTrends(item.id);
      setPlayerRegistrationTrends(res.data);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getPaymentAccountsData = async () => {
    setPaymentAccountsLoading(true);
    try {
      const [linkedRes, allRes] = await Promise.all([
        getPaymentAccountsByTournament(item.id),
        getAllPaymentAccounts(),
      ]);
      setPaymentAccounts(linkedRes.data || []);
      setAllPaymentAccounts(allRes.data || []);
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setPaymentAccountsLoading(false);
    }
  };

  const linkedIds = new Set(paymentAccounts.map((a) => a.id));
  const unlinkableAccounts = allPaymentAccounts.filter((a) => !linkedIds.has(a.id));

  const handleLinkAccount = async (accountId: string) => {
    try {
      await linkPaymentAccountToTournament(item.id, {
        paymentAccountId: accountId,
        isActive: true,
        displayOrder: paymentAccounts.length,
      });
      dispatch(setGeneralAlertMessage({ message: "Payment account linked", type: "success" }));
      setShowLinkSelect(false);
      await getPaymentAccountsData();
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.response?.data?.message || "Failed to link account", type: "error" }));
    }
  };

  const handleUnlinkAccount = async (accountId: string) => {
    try {
      await unlinkPaymentAccountFromTournament(item.id, accountId);
      dispatch(setGeneralAlertMessage({ message: "Payment account unlinked", type: "success" }));
      await getPaymentAccountsData();
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.response?.data?.message || "Failed to unlink account", type: "error" }));
    }
  };

  const getPromoCodesData = async () => {
    setPromoCodesLoading(true);
    try {
      const res = await getPromoCodesByTournament(item.id);
      setPromoCodes(res.data || []);
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setPromoCodesLoading(false);
    }
  };

  const getAccommodationsData = async () => {
    setAccommodationsLoading(true);
    try {
      const res = await getAccommodationsByTournament(item.id);
      setAccommodations(res.data || []);
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setAccommodationsLoading(false);
    }
  };

  const getData = async () => {
    // Users who are only bracket managers don't need to see player demographics, registration trends, payment accounts, or promo codes
    if (!isSuperAdmin(currentUser)) {
      return;
    }

    try {
      await getPlayerDemographicData();
      await getPlayerRegistrationTrendsData();
      await getPaymentAccountsData();
      await getPromoCodesData();
      await getAccommodationsData();
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleNativeShare = async (id: string, name: string) => {
    const url = `${process.env.REACT_APP_FRONTEND_URL}tournaments/${id}?register=true`;
    const text = "Register now using the link!";

    if (!navigator.share) {
      navigator.clipboard.writeText(url);
      await alert({ message: "Link copied to clipboard (sharing not supported)" });
      return;
    }

    try {
      let files: File[] | undefined;
      if (item.cover) {
        try {
          const res = await fetch(item.cover);
          const blob = await res.blob();
          const ext = (blob.type.split("/")[1] || "jpg").split("+")[0];
          const file = new File([blob], `${name}.${ext}`, { type: blob.type || "image/jpeg" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            files = [file];
          }
        } catch (err) {
          console.error("Failed to load cover image for share:", err);
        }
      }

      await navigator.share(files ? { title: name, text, url, files } : { title: name, text, url });
    } catch (err) {
      console.error("Share failed:", err);
    }
  };

  const updateStatus = async (value: string) => {
    try {
      const res = await editTournament(item.id, {
        registrationClosed: value === "closed" ? true : false,
        registrationOpen: value === "open" ? true : false,
        cover: item.cover,
      });
      setRegistrationStatus(res.data.registrationOpen ? "open" : res.data.registrationClosed ? "closed" : "not-open");
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleDialogClose = (update: boolean) => {
    setEdit(false);
    if (update) onDoneEdit();
  };

  const handlePublishChange = async () => {
    try {
      await editTournament(item.id, { isPublished: !item.isPublished });
      onDoneEdit();
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleDeleteTournament = async () => {
    const res = await deleteTournament(item.id);
    if (res.data && res.success) {
      dispatch(setGeneralAlertMessage({ message: res.message || "Tournament deleted successfully", type: "success" }));
      setShowDeleteDialog(false);
      navigate("/dashboard/tournaments");
    } else {
      dispatch(setGeneralAlertMessage({ message: res.message || "Failed to delete tournament", type: "error" }));
      throw new Error(res.message);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const { data, xKey } = getChartData();

  const playerDemographicGroupData: GroupData = {
    nationality:
      playerDemographics?.nationalityCounts?.map((item) => ({
        name: item.nationality,
        value: item.count,
      })) || [],
    gender:
      playerDemographics?.genderCounts?.map((item) => ({
        name: item.gender,
        value: item.count,
      })) || [],
    tshirtSize:
      playerDemographics?.tshirtSizeCounts?.map((item) => ({
        name: item.tshirtSize,
        value: item.count,
      })) || [],
  };

  const playerDemographicChartData: ChartItem[] = useMemo(() => {
    const selectedGroup = playerDemographicGroupData[playerDemographicOption as keyof GroupData];
    const sortedGroup = [...selectedGroup].sort((a, b) => b.value - a.value);

    const top3 = sortedGroup.slice(0, 5);
    const others = sortedGroup.slice(5);
    const othersTotal = others.reduce((sum, item) => sum + item.value, 0);

    const totalPlayers = playerDemographics?.totalPlayers || 1;

    const rawData = [
      ...top3.map((item) => ({
        name: item.name,
        value: item.value,
        rawPercent: (item.value / totalPlayers) * 100,
      })),
      ...(othersTotal > 0
        ? [
            {
              name: "Others",
              value: othersTotal,
              rawPercent: (othersTotal / totalPlayers) * 100,
            },
          ]
        : []),
    ];

    let remaining = 100;
    return rawData.map((item, index) => {
      const isLast = index === rawData.length - 1;
      const rounded = isLast ? remaining : Math.floor(item.rawPercent);
      remaining -= rounded;

      return {
        name: item.name,
        value: item.value,
        percent: rounded,
        color: item.name === "Others" ? "#99B7DE" : getColorForKey(item.name),
      };
    });
  }, [playerDemographicGroupData, playerDemographics, playerDemographicOption]);

  const sportIcon =
    item.type === "Volleyball" ? (
      <Volleyball size={18} />
    ) : item.type === "Beach Volleyball" ? (
      <SeaWaves size={18} />
    ) : item.type === "Padel" ? (
      <Baseball size={18} />
    ) : item.type === "Beach Tennis" ? (
      <Sun size={18} />
    ) : item.type === "Spikeball" ? (
      <CircleDot size={18} />
    ) : item.type === "Dodgeball" ? (
      <Target size={18} />
    ) : item.type === "Beach Football" ? (
      <FontAwesomeIcon icon={faFutbol} style={{ fontSize: 15 }} />
    ) : null;

  const addCategory = () =>
    (window.location.href = `${item.id}/add-category?name=${item.name}&type=${sportTypeToSlug(item.type)}`);

  const cardTitle = (title: string, count?: number) => (
    <span className="card-title-with-count">
      {title}
      {count !== undefined && <Tag tone="spark">{count}</Tag>}
    </span>
  );

  return (
    <div className="tournament-detail">
      <PageHeader
        className="tournament-detail-header"
        back={{ onClick: handleBack, label: getBackButtonText() }}
        title={item.name}
        subtitle={
          <span className="description">
            {readMore ? item.description : item.description.slice(0, 50)}
            {item.description.length > 50 ? "..." : ""}
            {item.description.length > 50 && (
              <Button variant="link" size="sm" className="read-more-text" onClick={() => setIsReadMore((prev) => !prev)}>
                {!readMore ? "Read more" : "Read less"}
              </Button>
            )}
          </span>
        }
        actions={
          <>
            {canManageTournaments(currentUser) ? (
              <Select
                aria-label="Registration status"
                value={registrationStatus}
                onChange={(e) => updateStatus(e.target.value)}
                className={`registration-status ${registrationStatus}`}
              >
                <option value={"open"}>Registration Open</option>
                <option value={"closed"}>Registration Closed</option>
                <option value={"not-open"}>Registration Not Open</option>
              </Select>
            ) : (
              <Tag size="md" className="registration-tag" tone={REGISTRATION_TONES[registrationStatus]}>
                {registrationStatus === "open" && "Registration Open"}
                {registrationStatus === "closed" && "Registration Closed"}
                {registrationStatus === "not-open" && "Registration Not Open"}
              </Tag>
            )}
            {canManageTournaments(currentUser) && (
              <Button
                variant={item.isPublished ? "ghost" : "primary"}
                className="publish-btn"
                onClick={() => handlePublishChange()}
              >
                {item.isPublished ? "Unpublish" : "Publish"}
              </Button>
            )}
            {canManageTournaments(currentUser) && (
              <Button variant="tint" className="edit-btn" icon={<EditOne size={18} />} onClick={() => setEdit(true)}>
                Edit
              </Button>
            )}
            <IconButton label="Share" icon={<Share size={18} />} onClick={() => handleNativeShare(item.id, item.name)} />
            {canManageTournaments(currentUser) && !item.isPublished && (
              <IconButton
                variant="danger"
                label="Delete tournament"
                icon={<Trash2 size={18} />}
                onClick={() => setShowDeleteDialog(true)}
              />
            )}
          </>
        }
      >
        <div className="tournament-info-container">
          <div className="avatar-container">
            {item.cover ? <img src={item.cover} alt="" /> : <Trophy size={24} />}
          </div>
          {/* The wrappers are display: contents on desktop (one flat strip); on phones they stack beside the cover */}
          <div className="meta-body">
            <div className="meta-tags">
              <Tag tone={item.isPublished ? "ok" : "neutral"}>{item.isPublished ? "Published" : "Not Published"}</Tag>
              <Tag tone={SPORT_TONES[item.type] ?? "neutral"} icon={sportIcon}>
                {item.type}
              </Tag>
            </div>
            <div className="meta-facts">
              <div className="info-container">
                <Calendar size={18} />
                <p>Starts {moment(item.startDate).format("DD MMM YYYY")}</p>
              </div>
              <div className="info-container">
                <CalendarCheck size={18} />
                <p>End {moment(item.endDate).format("DD MMM YYYY")}</p>
              </div>
              <div className="info-container info-container--location">
                <Location size={18} />
                {item.location ? (
                  item.location.googleMapsLink ? (
                    <a
                      className="location-link"
                      href={item.location.googleMapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.location.name}
                    </a>
                  ) : (
                    <p>{item.location.name}</p>
                  )
                ) : (
                  <p>-</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageHeader>

      <div className="tournament-data">
        <div className="first-row">
          <Card
            className="categories-card"
            title={cardTitle("CATEGORIES", item.categories.length > 0 ? item.categories.length : undefined)}
            actions={
              item.categories.length > 0 && (
                <Button variant="tint" size="sm" onClick={() => navigate(`categories`)}>
                  View all
                </Button>
              )
            }
          >
            {item.categories.length > 0 ? (
              <div className="card-list">
                {item.categories.slice(0, 3).map((category, id) => (
                  <button type="button" className="list-item category-item" onClick={() => navigate(`${category.id}`)} key={id}>
                    <span className="list-item__icon">
                      {category?.format === "League" ? (
                        <ListNumber size={22} />
                      ) : category?.format === "Knockout" ? (
                        <GitMerge size={22} />
                      ) : category?.format === "League-Knockout" ? (
                        <Ribbon size={22} />
                      ) : category.format === "Groups-Knockout" ? (
                        <Menu size={22} />
                      ) : (
                        <Question size={22} />
                      )}
                    </span>
                    <span className="list-item__text">
                      <span className="list-item__name">{category.categoryName}</span>
                      <span className="list-item__meta">
                        <span>{category?.format?.replace("-", " + ")}</span>
                        <span className="separator" />
                        <span>
                          {category.allowIndividualRegistration
                            ? `${category.numOfPlayers} ${category.numOfPlayers === 1 ? "Player" : "Players"}`
                            : `${category.numOfTeams} ${category.numOfTeams === 1 ? "Team" : "Teams"}`}
                        </span>
                      </span>
                    </span>
                  </button>
                ))}
                {canManageCategories(currentUser) && (
                  <Button variant="ghost" block icon={<Plus size={18} />} className="create-category-btn" onClick={addCategory}>
                    Create new category
                  </Button>
                )}
              </div>
            ) : (
              <EmptyState
                compact
                title="No categories"
                description={
                  canManageCategories(currentUser)
                    ? "There are no categories in this tournament yet! Start adding categories"
                    : "There are no categories in this tournament yet"
                }
                action={
                  canManageCategories(currentUser) && (
                    <Button size="sm" icon={<Plus size={16} />} onClick={addCategory}>
                      Add category
                    </Button>
                  )
                }
              />
            )}
          </Card>
          {isSuperAdmin(currentUser) && (
            <Card
              className="registration-trends-card"
              title="Registration Trends"
              actions={
                playersCount > 0 && (
                  <Select
                    size="sm"
                    aria-label="Registration Trends"
                    value={registrationTrendOption}
                    onChange={(evt) => setRegistrationTrendOption(evt.target.value)}
                  >
                    <option value="thisMonth">This Month</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </Select>
                )
              }
            >
              {/* The box sets the chart height: 300px, 200px on phones */}
              <div className="trends-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={CHART.grid} />
                    <XAxis
                      dataKey={xKey}
                      className="xaxis"
                      tickLine={false}
                      axisLine={{ stroke: CHART.grid }}
                      tick={{ fill: CHART.axis, fontSize: 12 }}
                    />
                    <YAxis className="yaxis" axisLine={false} tickLine={false} tick={{ fill: CHART.axis, fontSize: 11 }} />
                    <Tooltip
                      cursor={{ fill: CHART.cursor }}
                      contentStyle={{
                        border: `1px solid ${CHART.grid}`,
                        borderRadius: 4,
                        boxShadow: "0 10px 30px rgba(14, 17, 22, 0.14)",
                        fontSize: 13,
                      }}
                      labelStyle={{ color: CHART.ink, fontWeight: 600 }}
                      itemStyle={{ color: CHART.bar }}
                    />
                    <Bar dataKey="value" fill={CHART.bar} radius={[3, 3, 0, 0]} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
        </div>
        <div className="second-row">
          {isSuperAdmin(currentUser) && (
            <Card
              className="payment-accounts-card"
              title={cardTitle(
                "PAYMENT ACCOUNTS",
                !paymentAccountsLoading && paymentAccounts.length > 0 ? paymentAccounts.length : undefined
              )}
              actions={
                !paymentAccountsLoading &&
                unlinkableAccounts.length > 0 && (
                  <Button
                    variant={showLinkSelect ? "ghost" : "tint"}
                    size="sm"
                    onClick={() => setShowLinkSelect(!showLinkSelect)}
                  >
                    {showLinkSelect ? "Cancel" : "+ Link"}
                  </Button>
                )
              }
            >
              <div className="card-list">
                {showLinkSelect && (
                  <div className="link-select-container">
                    <Select
                      block
                      aria-label="Select an account to link"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) handleLinkAccount(e.target.value);
                      }}
                    >
                      <option value="" disabled>
                        Select an account to link...
                      </option>
                      {unlinkableAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.accountName} ({PaymentAccountTypeLabels[a.accountType]})
                        </option>
                      ))}
                    </Select>
                  </div>
                )}
                {paymentAccountsLoading ? (
                  <Loader minHeight={120} />
                ) : paymentAccounts.length > 0 ? (
                  <>
                    {paymentAccounts.map((account) => (
                      <div className="list-item payment-account-item" key={account.id}>
                        <span className="list-item__icon">
                          <CreditCard size={22} />
                        </span>
                        <span className="list-item__text">
                          <span className="list-item__name">{account.accountName}</span>
                          <span className="list-item__meta">
                            <span>{PaymentAccountTypeLabels[account.accountType]}</span>
                            {account.currency && (
                              <>
                                <span className="separator" />
                                <span>{account.currency}</span>
                              </>
                            )}
                            <span className="separator" />
                            <Tag tone={account.isActive ? "ok" : "neutral"}>{account.isActive ? "Active" : "Inactive"}</Tag>
                          </span>
                        </span>
                        <IconButton
                          variant="danger"
                          size="sm"
                          label="Unlink from tournament"
                          icon={<X size={16} />}
                          onClick={() => handleUnlinkAccount(account.id)}
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <EmptyState
                    compact
                    icon={<CreditCard />}
                    title="No payment accounts linked"
                    description="Link existing payment accounts or create new ones from the Payment Accounts page"
                  />
                )}
              </div>
            </Card>
          )}
          {isSuperAdmin(currentUser) && (
            <Card
              className="promo-codes-card"
              title={cardTitle("PROMO CODES", !promoCodesLoading && promoCodes.length > 0 ? promoCodes.length : undefined)}
              actions={
                !promoCodesLoading &&
                promoCodes.length > 0 && (
                  <Button variant="tint" size="sm" onClick={() => navigate(`promo-codes`)}>
                    View all
                  </Button>
                )
              }
            >
              {promoCodesLoading ? (
                <Loader minHeight={120} />
              ) : promoCodes.length > 0 ? (
                <div className="card-list">
                  {promoCodes.slice(0, 3).map((promoCode) => (
                    <div className="list-item promo-code-item" key={promoCode.id}>
                      <span className="list-item__icon">
                        <TagIcon size={22} />
                      </span>
                      <span className="list-item__text">
                        <span className="list-item__name promo-name">{promoCode.code}</span>
                        <span className="list-item__meta">
                          <span>
                            {promoCode.discountPercentage
                              ? `${promoCode.discountPercentage}% off`
                              : promoCode.discountAmount
                              ? `${promoCode.discountAmount} off`
                              : "N/A"}
                          </span>
                          <span className="separator" />
                          <Tag tone={promoCode.isActive ? "ok" : "neutral"}>{promoCode.isActive ? "Active" : "Inactive"}</Tag>
                        </span>
                      </span>
                    </div>
                  ))}
                  <Button variant="ghost" block icon={<Plus size={18} />} onClick={() => navigate(`promo-codes`)}>
                    Manage promo codes
                  </Button>
                </div>
              ) : (
                <EmptyState
                  compact
                  icon={<TagIcon />}
                  title="No promo codes"
                  description="Add promo codes to offer discounts during team registration"
                  action={
                    <Button size="sm" icon={<Plus size={16} />} onClick={() => navigate(`promo-codes`)}>
                      Add promo code
                    </Button>
                  }
                />
              )}
            </Card>
          )}
          {isSuperAdmin(currentUser) && !accommodationsLoading && accommodations.length > 0 && (
            <Card
              className="accommodations-card"
              title={cardTitle("ACCOMMODATIONS", accommodations.length)}
              actions={
                <Button variant="tint" size="sm" onClick={() => navigate(`accommodations`)}>
                  View all
                </Button>
              }
            >
              <div className="card-list">
                <div className="accommodations-insights">
                  <StatCard tone="brand" icon={<Building size={18} />} label="Total Bookings" value={accommodations.length} />
                  <StatCard
                    tone="brand"
                    icon={<Building size={18} />}
                    label="Total Rooms"
                    value={accommodations.reduce((sum, acc) => sum + acc.accommodationDetails.numberOfRooms, 0)}
                  />
                  <StatCard
                    tone="brand"
                    icon={<Building size={18} />}
                    label="Total Revenue"
                    value={
                      <>
                        {accommodations.length > 0 && accommodations[0].pricing.currency}{" "}
                        {accommodations.reduce((sum, acc) => sum + acc.pricing.totalAfterDiscount, 0).toLocaleString()}
                      </>
                    }
                  />
                </div>
                <Button variant="ghost" block icon={<Plus size={18} />} onClick={() => navigate(`accommodations`)}>
                  View all accommodations
                </Button>
              </div>
            </Card>
          )}
          {isSuperAdmin(currentUser) && <TournamentGalleryCard tournamentName={item.name} tournamentId={item.id} />}
        </div>
        {isSuperAdmin(currentUser) && (
          <div className="bottom-row">
            <div className="players-teams-card">
              <StatCard className="players-card" label="PLAYERS" value={playersCount} />
              <StatCard
                className="teams-card"
                tone="brand"
                label="NATIONALITIES"
                value={playerDemographics?.nationalityCounts.length}
              />
            </div>
            <Card className="player-demographics-card" title="Player Demographic">
              {playerDemographicChartData.length > 0 ? (
                <>
                  <div className="group-by-container">
                    <p>Group by</p>
                    <Select
                      size="sm"
                      aria-label="Group by"
                      value={playerDemographicOption}
                      onChange={(evt) => setPlayerDemographicOption(evt.target.value)}
                    >
                      <option value="nationality">Nationality</option>
                      <option value="tshirtSize">Tshirt Size</option>
                      <option value="gender">Gender</option>
                    </Select>
                  </div>
                  <DemographicsBar data={playerDemographicChartData} />
                </>
              ) : (
                <EmptyState compact title="There are no registered players in this tournament yet!" />
              )}
            </Card>
          </div>
        )}
      </div>
      <TournamentEditDialog item={item} isOpen={edit} onClose={handleDialogClose} />
      <DeleteTournamentDialog
        isOpen={showDeleteDialog}
        tournamentName={item.name}
        onDelete={handleDeleteTournament}
        onClose={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};

export default TournamentDetail;
